// OpenCV.js loader + document detection / processing helpers.
// Ported from scanner-app, adapted to the vendored OpenCV build.
import cvModule from '../vendor/opencv'

let cvPromise = null

/** Resolves with the initialized cv namespace. */
export function loadCV(waitTimeMs = 30000, stepTimeMs = 100) {
  if (!cvPromise) {
    cvPromise = new Promise((resolve, reject) => {
      const cv = cvModule
      // Emscripten modules expose a `then` that resolves with the module itself.
      // Resolving a Promise with such a thenable causes infinite thenable
      // adoption (a microtask loop that hangs the page), so strip it first.
      const done = () => {
        try {
          delete cv.then
        } catch {
          /* ignore */
        }
        resolve(cv)
      }
      if (cv && cv.Mat) return done()
      let timeSpentMs = 0
      const interval = setInterval(() => {
        if (cv && cv.Mat) {
          clearInterval(interval)
          done()
        } else if (timeSpentMs > waitTimeMs) {
          clearInterval(interval)
          reject(new Error('OpenCV failed to load'))
        } else {
          timeSpentMs += stepTimeMs
        }
      }, stepTimeMs)
    })
  }
  return cvPromise
}

/** Order 4 points as [topLeft, topRight, bottomRight, bottomLeft]. */
export function orderCorners(pts) {
  const bySum = [...pts].sort((a, b) => a.x + a.y - (b.x + b.y))
  const tl = bySum[0]
  const br = bySum[3]
  const byDiff = [...pts].sort((a, b) => a.y - a.x - (b.y - b.x))
  const tr = byDiff[0]
  const bl = byDiff[3]
  return [tl, tr, br, bl]
}

/**
 * Try to find the best 4-point quadrilateral from a set of contours.
 * Tries multiple epsilon values for polygon approximation to increase
 * robustness across different image qualities.
 * @returns {{pts: Array, area: number}|null}
 */
function findBestQuadFromContours(cv, contours, minArea, epsilons, maxArea = Infinity) {
  let best = null
  let bestArea = minArea

  for (let i = 0; i < contours.size(); i++) {
    const c = contours.get(i)
    const area = cv.contourArea(c)
    if (area > bestArea && area < maxArea) {
      const peri = cv.arcLength(c, true)
      let found = false
      for (const eps of epsilons) {
        const approx = new cv.Mat()
        cv.approxPolyDP(c, approx, eps * peri, true)
        if (approx.rows === 4 && cv.isContourConvex(approx)) {
          if (best) best.delete()
          best = approx
          bestArea = area
          found = true
          break
        } else {
          approx.delete()
        }
      }
      if (!found) {
        // No good quad found at any epsilon for this contour
      }
    }
    c.delete()
  }

  if (!best) return null
  const pts = []
  for (let i = 0; i < 4; i++) {
    pts.push({ x: best.data32S[i * 2], y: best.data32S[i * 2 + 1] })
  }
  best.delete()
  return { pts, area: bestArea }
}

/**
 * Collect all reasonable 4-point quads from a contour set into `out`.
 * Used by the scoring pipeline, which ranks candidates globally instead of
 * keeping only the largest.
 */
function collectQuadsFromContours(cv, contours, minArea, epsilons, maxArea, out) {
  for (let i = 0; i < contours.size(); i++) {
    const c = contours.get(i)
    const area = cv.contourArea(c)
    if (area > minArea && area < maxArea) {
      const peri = cv.arcLength(c, true)
      for (const eps of epsilons) {
        const approx = new cv.Mat()
        cv.approxPolyDP(c, approx, eps * peri, true)
        if (approx.rows === 4 && cv.isContourConvex(approx)) {
          const pts = []
          for (let j = 0; j < 4; j++) {
            pts.push({ x: approx.data32S[j * 2], y: approx.data32S[j * 2 + 1] })
          }
          approx.delete()
          if (isReasonableQuad(pts)) out.push({ pts, area })
          break
        }
        approx.delete()
      }
    }
    c.delete()
  }
}

/**
 * Validate that a quadrilateral has reasonable geometry for a document:
 * - No interior angle too acute (<30°) or too obtuse (>150°)
 * - Aspect ratio within reasonable bounds (not too thin)
 */
export function isReasonableQuad(pts) {
  for (let i = 0; i < 4; i++) {
    const a = pts[(i + 3) % 4]
    const b = pts[i]
    const c = pts[(i + 1) % 4]
    const ba = { x: a.x - b.x, y: a.y - b.y }
    const bc = { x: c.x - b.x, y: c.y - b.y }
    const dot = ba.x * bc.x + ba.y * bc.y
    const magA = Math.hypot(ba.x, ba.y)
    const magC = Math.hypot(bc.x, bc.y)
    if (magA === 0 || magC === 0) return false
    const cosAngle = dot / (magA * magC)
    const angle = Math.acos(Math.max(-1, Math.min(1, cosAngle))) * (180 / Math.PI)
    if (angle < 30 || angle > 150) return false
  }
  return true
}

/** Shoelace area of a 4-point polygon. */
function quadArea(pts) {
  let area = 0
  for (let i = 0; i < 4; i++) {
    const a = pts[i]
    const b = pts[(i + 1) % 4]
    area += a.x * b.y - b.x * a.y
  }
  return Math.abs(area) / 2
}

/** Intersection point of two infinite lines given as segments, or null. */
function lineIntersection(p1, p2, p3, p4) {
  const d = (p1.x - p2.x) * (p3.y - p4.y) - (p1.y - p2.y) * (p3.x - p4.x)
  if (Math.abs(d) < 1e-9) return null
  const a = p1.x * p2.y - p1.y * p2.x
  const b = p3.x * p4.y - p3.y * p4.x
  return {
    x: (a * (p3.x - p4.x) - (p1.x - p2.x) * b) / d,
    y: (a * (p3.y - p4.y) - (p1.y - p2.y) * b) / d,
  }
}

/**
 * Detect document candidates from long straight lines (probabilistic Hough).
 *
 * Contour-based strategies need a closed outline; a hand/shadow crossing the
 * document, or glare hugging its border, breaks the contour and no quad is
 * ever proposed. Document *sides* are still long straight edges, though.
 * Near-collinear segments are merged, then quads are formed from every pair
 * of near-parallel well-separated lines crossed with every roughly
 * perpendicular such pair. When no perpendicular pair exists (short receipt
 * ends often vanish in glare/shadow), fall back to the parallel pair's own
 * extents. The scoring pipeline validates the candidates.
 */
function collectHoughQuads(cv, src, minArea, maxArea, out) {
  if (typeof cv.HoughLinesP !== 'function') return
  const gray = new cv.Mat()
  const blurred = new cv.Mat()
  const edges = new cv.Mat()
  const lines = new cv.Mat()
  const rgb = new cv.Mat()
  const hsv = new cv.Mat()
  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY)
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0)
    cv.cvtColor(src, rgb, cv.COLOR_RGBA2RGB)
    cv.cvtColor(rgb, hsv, cv.COLOR_RGB2HSV)
    const hd = hsv.data
    const cols = src.cols
    const rows = src.rows
    // Scene-adaptive paper brightness (95th percentile of V).
    const totalPx = cols * rows
    const hist = new Array(256).fill(0)
    for (let i = 0; i < totalPx; i++) hist[hd[i * 3 + 2]]++
    let acc = 0
    let v95 = 255
    for (let v = 0; v < 256; v++) {
      acc += hist[v]
      if (acc >= totalPx * 0.95) {
        v95 = v
        break
      }
    }
    const whiteVal = Math.max(60, v95 * 0.55)
    const strictVal = Math.max(whiteVal, v95 * 0.85)
    // A document side has paper along (at least) one flank. Scratches on a
    // table, marble veins and other line clutter have background on both
    // sides, so demand a mostly-paper flank before considering a segment.
    // Also returns strict-paper flank fractions: a boundary with true paper
    // on exactly one side (asymmetric) is the signature of a document edge,
    // while sheen/vein lines are symmetric.
    const flankStats = (p, q) => {
      const len = Math.hypot(q.x - p.x, q.y - p.y) || 1
      const nx = (-(q.y - p.y) / len) * 7
      const ny = ((q.x - p.x) / len) * 7
      let a = 0
      let b = 0
      let sa = 0
      let sb = 0
      let m = 0
      for (let t = 1; t < 12; t++) {
        const px = p.x + ((q.x - p.x) * t) / 12
        const py = p.y + ((q.y - p.y) * t) / 12
        const x1 = Math.round(px + nx)
        const y1 = Math.round(py + ny)
        const x2 = Math.round(px - nx)
        const y2 = Math.round(py - ny)
        if (x1 < 0 || y1 < 0 || x1 >= cols || y1 >= rows) continue
        if (x2 < 0 || y2 < 0 || x2 >= cols || y2 >= rows) continue
        m++
        const i1 = (y1 * cols + x1) * 3
        const i2 = (y2 * cols + x2) * 3
        if (hd[i1 + 1] <= 110 && hd[i1 + 2] >= whiteVal) a++
        if (hd[i2 + 1] <= 110 && hd[i2 + 2] >= whiteVal) b++
        if (hd[i1 + 1] <= 60 && hd[i1 + 2] >= strictVal) sa++
        if (hd[i2 + 1] <= 60 && hd[i2 + 2] >= strictVal) sb++
      }
      if (m < 4) return null
      return {
        paperFlank: Math.max(a, b) / m >= 0.6,
        asym: Math.max(sa, sb) / m >= 0.6 && Math.min(sa, sb) / m <= 0.3,
      }
    }
    const minDim = Math.min(src.cols, src.rows)
    const angDiff = (a, b) => {
      const d = Math.abs(a - b) % Math.PI
      return Math.min(d, Math.PI - d)
    }
    // Perpendicular distance from segment t's midpoint to s's infinite line.
    const lineDist = (s, t) => {
      const nx = -(s.q.y - s.p.y)
      const ny = s.q.x - s.p.x
      const nl = Math.hypot(nx, ny) || 1
      const mx = (t.p.x + t.q.x) / 2 - s.p.x
      const my = (t.p.y + t.q.y) / 2 - s.p.y
      return Math.abs((nx * mx + ny * my) / nl)
    }

    let segs = []
    for (const [lo, hi] of [[50, 150], [20, 60]]) {
      cv.Canny(blurred, edges, lo, hi)
      cv.HoughLinesP(edges, lines, 1, Math.PI / 180, 60, minDim * 0.25, minDim * 0.06)
      for (let i = 0; i < lines.rows; i++) {
        const x1 = lines.data32S[i * 4]
        const y1 = lines.data32S[i * 4 + 1]
        const x2 = lines.data32S[i * 4 + 2]
        const y2 = lines.data32S[i * 4 + 3]
        const p = { x: x1, y: y1 }
        const q = { x: x2, y: y2 }
        const st = flankStats(p, q)
        if (!st || !st.paperFlank) continue
        const len = Math.hypot(x2 - x1, y2 - y1)
        segs.push({ p, q, len, angle: Math.atan2(y2 - y1, x2 - x1), asym: st.asym })
      }
    }
    if (segs.length < 2) return

    // Merge near-collinear segments (Hough emits several per physical edge,
    // and shadows/glare split one edge into pieces): each group keeps the
    // longest segment's line but grows its extent to the union of all
    // members' projections along it, reconnecting interrupted document sides.
    // Lines are capped per orientation bucket so one dominant direction
    // (e.g. a family of parallel table scratches) cannot crowd out the
    // document's own edges.
    // Boundary-like segments (strict paper on exactly one flank) get bucket
    // priority over merely-long ones: long clutter lines (glare sheen bands,
    // scratch families) must not crowd document edges out of the buckets.
    segs.sort((a, b) => (b.asym === a.asym ? b.len - a.len : b.asym ? 1 : -1))
    const merged = []
    const bucketCount = new Array(12).fill(0)
    const bucketOf = (angle) => {
      let a = angle % Math.PI
      if (a < 0) a += Math.PI
      return Math.min(11, Math.floor((a / Math.PI) * 12))
    }
    for (const s of segs) {
      let host = null
      for (const m of merged) {
        if (angDiff(s.angle, m.angle) < Math.PI / 36 && lineDist(m, s) < minDim * 0.015) {
          host = m
          break
        }
      }
      if (!host) {
        const bucket = bucketOf(s.angle)
        if (merged.length < 36 && bucketCount[bucket] < 8) {
          bucketCount[bucket]++
          merged.push({ p: { ...s.p }, q: { ...s.q }, len: s.len, angle: s.angle })
        }
        continue
      }
      // Extend host's extent along its own direction.
      const dx = host.q.x - host.p.x
      const dy = host.q.y - host.p.y
      const dl = Math.hypot(dx, dy) || 1
      const ux = dx / dl
      const uy = dy / dl
      let tMin = 0
      let tMax = dl
      for (const pt of [s.p, s.q]) {
        const t = (pt.x - host.p.x) * ux + (pt.y - host.p.y) * uy
        if (t < tMin) tMin = t
        if (t > tMax) tMax = t
      }
      const bx = host.p.x
      const by = host.p.y
      host.q = { x: bx + ux * tMax, y: by + uy * tMax }
      host.p = { x: bx + ux * tMin, y: by + uy * tMin }
      host.len = tMax - tMin
    }

    const seen = new Set()
    const push = (rawPts) => {
      // Clamp to the frame: side support is only measurable on real pixels,
      // and off-frame corners let junk quads collect "free" support from
      // skipped samples. Contour strategies produce frame-clipped quads for
      // documents extending past the view anyway.
      const ordered = orderCorners(
        rawPts.map((p) => ({
          x: Math.min(src.cols - 1, Math.max(0, Math.round(p.x))),
          y: Math.min(src.rows - 1, Math.max(0, Math.round(p.y))),
        }))
      )
      const area = quadArea(ordered)
      if (area < minArea || area > maxArea) return
      if (!isReasonableQuad(ordered)) return
      const key = ordered.map((p) => `${p.x >> 4},${p.y >> 4}`).join(';')
      if (seen.has(key)) return
      seen.add(key)
      out.push({ pts: ordered, area })
    }

    // All near-parallel, well-separated pairs.
    const pairs = []
    for (let i = 0; i < merged.length; i++) {
      for (let j = i + 1; j < merged.length; j++) {
        if (angDiff(merged[i].angle, merged[j].angle) > Math.PI / 12) continue
        if (lineDist(merged[i], merged[j]) < minDim * 0.1) continue
        pairs.push([merged[i], merged[j]])
      }
    }

    for (const [a1, a2] of pairs) {
      for (const [b1, b2] of pairs) {
        // Perpendicular-ish cross pair.
        if (angDiff(a1.angle, b1.angle) < Math.PI / 3) continue
        const c1 = lineIntersection(a1.p, a1.q, b1.p, b1.q)
        const c2 = lineIntersection(a1.p, a1.q, b2.p, b2.q)
        const c3 = lineIntersection(a2.p, a2.q, b2.p, b2.q)
        const c4 = lineIntersection(a2.p, a2.q, b1.p, b1.q)
        if (!c1 || !c2 || !c3 || !c4) continue
        if (
          [c1, c2, c3, c4].some(
            (p) => p.x < -src.cols * 0.2 || p.y < -src.rows * 0.2 || p.x > src.cols * 1.2 || p.y > src.rows * 1.2
          )
        )
          continue
        push([c1, c2, c3, c4])
      }
      {
        // Also span the combined extents of the pair itself: cross pairs are
        // often unrelated clutter lines, while the pair's own extents (plus
        // the paper-strip extension below) recover the document directly.
        const dx = a1.q.x - a1.p.x
        const dy = a1.q.y - a1.p.y
        const dl = Math.hypot(dx, dy) || 1
        const ux = dx / dl
        const uy = dy / dl
        let tMin = Infinity
        let tMax = -Infinity
        for (const pt of [a1.p, a1.q, a2.p, a2.q]) {
          const t = (pt.x - a1.p.x) * ux + (pt.y - a1.p.y) * uy
          if (t < tMin) tMin = t
          if (t > tMax) tMax = t
        }
        const proj = (line, t) => {
          // Intersection of `line` with the perpendicular through the point
          // at parameter t along u (measured from a1.p).
          const base = { x: a1.p.x + ux * t, y: a1.p.y + uy * t }
          const tip = { x: base.x - uy, y: base.y + ux }
          return lineIntersection(line.p, line.q, base, tip) || base
        }
        push([proj(a1, tMin), proj(a1, tMax), proj(a2, tMin), proj(a2, tMax)])

        // Edges are often only partially visible (glare/shadow ate the
        // rest), leaving a quad too small to qualify. Extend along the pair
        // direction while the strip between the lines stays paper-like: the
        // document continues even where its side edges vanished.
        // The strip test uses near-v95 brightness (true paper, excluding
        // bright-ish glare sheen which sits below it) but only demands half
        // the samples pass, since printed text rows darken the rest.
        const strictVal = Math.max(whiteVal, v95 * 0.85)
        const stripIsPaper = (t) => {
          const e1 = proj(a1, t)
          const e2 = proj(a2, t)
          let white = 0
          let m = 0
          for (let k = 1; k < 11; k++) {
            const x = Math.round(e1.x + ((e2.x - e1.x) * k) / 11)
            const y = Math.round(e1.y + ((e2.y - e1.y) * k) / 11)
            if (x < 0 || y < 0 || x >= cols || y >= rows) return false
            m++
            const idx = (y * cols + x) * 3
            if (hd[idx + 1] <= 60 && hd[idx + 2] >= strictVal) white++
          }
          return m > 0 && white / m >= 0.5
        }
        const step = Math.max(6, Math.round(minDim * 0.02))
        const maxT = Math.hypot(cols, rows)
        let lo2 = tMin
        let hi2 = tMax
        while (hi2 - tMin < maxT && stripIsPaper(hi2 + step)) hi2 += step
        while (tMax - lo2 < maxT && stripIsPaper(lo2 - step)) lo2 -= step
        if (hi2 !== tMax || lo2 !== tMin) {
          push([proj(a1, lo2), proj(a1, hi2), proj(a2, lo2), proj(a2, hi2)])
        }
      }
    }
  } finally {
    gray.delete()
    blurred.delete()
    edges.delete()
    lines.delete()
    rgb.delete()
    hsv.delete()
  }
}

/**
 * Detect document candidates as bright, low-saturation (white/gray paper)
 * regions. This is the most reliable signal when the background is busy
 * (wood grain, cables, etc.) but the document itself is plain white paper.
 *
 * Returns an array of {pts, area} candidates (may be empty).
 */
function collectWhiteRegionQuads(cv, src, minArea, maxArea) {
  // Requires color-space ops not present in every cv build (or test mock);
  // skip the strategy gracefully if unavailable.
  if (
    typeof cv.inRange !== 'function' ||
    typeof cv.convexHull !== 'function' ||
    typeof cv.Scalar !== 'function'
  ) {
    return []
  }
  const rgb = new cv.Mat()
  const hsv = new cv.Mat()
  const mask = new cv.Mat()
  const morphed = new cv.Mat()
  const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(9, 9))
  // Large opening kernel: removes thin white structures (cables, pens,
  // reflections) that touch the document and would distort its outline.
  // Sized relative to the frame so behavior is resolution-independent.
  const openSize = Math.max(9, Math.round(Math.min(src.cols, src.rows) * 0.035)) | 1
  const openKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(openSize, openSize))
  const contours = new cv.MatVector()
  const hierarchy = new cv.Mat()

  try {
    cv.cvtColor(src, rgb, cv.COLOR_RGBA2RGB)
    cv.cvtColor(rgb, hsv, cv.COLOR_RGB2HSV)

    // Adapt brightness cutoffs to the scene: the document should be among the
    // brightest surfaces, so anchor thresholds to a high percentile of the
    // V (brightness) channel instead of fixed values. This handles both dim
    // rooms (where fixed cutoffs match nothing, then everything) and glare
    // (which inflates the maximum but barely moves the 95th percentile).
    const vData = hsv.data
    const hist = new Array(256).fill(0)
    const totalPx = hsv.rows * hsv.cols
    for (let i = 0; i < totalPx; i++) hist[vData[i * 3 + 2]]++
    let acc = 0
    let v95 = 255
    for (let v = 0; v < 256; v++) {
      acc += hist[v]
      if (acc >= totalPx * 0.95) {
        v95 = v
        break
      }
    }

    // [maxSaturation, minBrightness] pairs, strict to loose. minBrightness is
    // relative to v95 so "white" means "bright for this scene". The first,
    // near-v95 cutoff isolates paper from bright-ish glare sheen (e.g. light
    // reflecting off a dark table around a receipt), which sits just below
    // the paper's brightness.
    const cutoffs = [
      [60, Math.round(v95 * 0.9)],
      [60, Math.max(80, Math.round(v95 * 0.85))],
      [80, Math.max(70, Math.round(v95 * 0.72))],
      [100, Math.max(60, Math.round(v95 * 0.58))],
    ]

    // Collect every plausible rectangular blob across all cutoffs; the
    // scoring pipeline picks the winner.
    const out = []

    for (const [maxSat, minVal] of cutoffs) {
      const lo = new cv.Mat(hsv.rows, hsv.cols, hsv.type(), new cv.Scalar(0, 0, minVal))
      const hi = new cv.Mat(hsv.rows, hsv.cols, hsv.type(), new cv.Scalar(180, maxSat, 255))
      cv.inRange(hsv, lo, hi, mask)
      lo.delete(); hi.delete()

      // Several morphology variants per cutoff; each suits a different
      // failure mode, and the scoring pipeline arbitrates:
      // - CLOSE then OPEN: bridges text/QR gaps first — best when the
      //   document mask is fragmented.
      // - OPEN then CLOSE: kills speckle first — best when bright clutter
      //   sits next to the document and CLOSE would weld it on first.
      // - Directional strong OPENs (tall / wide bars): shear off glare bloom
      //   welded to one flank of an elongated document (e.g. a receipt with
      //   a horizontal glare band across it — the tall bar keeps the
      //   vertical paper stem and erases the band, and vice versa). The bar
      //   is a quarter of the frame so wide bloom bands are fully erased.
      const strongLen = Math.round(Math.min(src.cols, src.rows) * 0.25)
      const tallKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(Math.max(3, openSize >> 1), strongLen))
      const wideKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(strongLen, Math.max(3, openSize >> 1)))
      const variants = ['closeFirst', 'openFirst', 'tall', 'wide']
      for (const variant of variants) {
        if (variant === 'closeFirst') {
          cv.morphologyEx(mask, morphed, cv.MORPH_CLOSE, kernel)
          cv.morphologyEx(morphed, morphed, cv.MORPH_OPEN, openKernel)
        } else if (variant === 'openFirst') {
          cv.morphologyEx(mask, morphed, cv.MORPH_OPEN, openKernel)
          cv.morphologyEx(morphed, morphed, cv.MORPH_CLOSE, kernel)
        } else {
          cv.morphologyEx(mask, morphed, cv.MORPH_CLOSE, kernel)
          cv.morphologyEx(morphed, morphed, cv.MORPH_OPEN, variant === 'tall' ? tallKernel : wideKernel)
        }
        cv.findContours(morphed, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE)

      for (let i = 0; i < contours.size(); i++) {
        const blob = contours.get(i)
        const blobArea = cv.contourArea(blob)
        if (blobArea < minArea || blobArea > maxArea) {
          blob.delete()
          continue
        }

        const hull = new cv.Mat()
        cv.convexHull(blob, hull)
        const peri = cv.arcLength(hull, true)
        // Widen epsilon until the hull collapses to 4 points.
        let pts = null
        for (const eps of [0.02, 0.03, 0.05, 0.08, 0.1]) {
          const approx = new cv.Mat()
          cv.approxPolyDP(hull, approx, eps * peri, true)
          if (approx.rows === 4 && cv.isContourConvex(approx)) {
            const cand = []
            for (let j = 0; j < 4; j++) {
              cand.push({ x: approx.data32S[j * 2], y: approx.data32S[j * 2 + 1] })
            }
            approx.delete()
            if (isReasonableQuad(cand)) pts = cand
            break
          }
          approx.delete()
        }
        hull.delete()
        blob.delete()
        if (!pts) continue

        // The blob must mostly fill its fitted quad to be a document.
        const qArea = quadArea(pts)
        if (qArea <= 0) continue
        if (blobArea / qArea < 0.7) continue
        out.push({ pts, area: blobArea })
      }
      }
      tallKernel.delete()
      wideKernel.delete()
    }
    return out
  } finally {
    rgb.delete()
    hsv.delete()
    mask.delete()
    morphed.delete()
    kernel.delete()
    openKernel.delete()
    contours.delete()
    hierarchy.delete()
  }
}

/**
 * Rank candidate quads and return the corners of the best one (ordered
 * TL,TR,BR,BL), or null if nothing passes the quality gates.
 *
 * A real document quad has two strong properties that junk quads lack:
 * - its interior is overwhelmingly white/low-saturation (paper), and
 * - every one of its four sides lies on a real image edge (the physical
 *   document boundary). Glare spilling onto a table produces bright regions
 *   whose fitted quad crosses smooth wood — no edge support — so it loses.
 */
function pickBestCandidate(cv, src, candidates, diag) {
  if (!candidates.length) return null
  const rgb = new cv.Mat()
  const hsv = new cv.Mat()
  const gray = new cv.Mat()
  const blurred = new cv.Mat()
  const edges = new cv.Mat()
  const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(9, 9))
  try {
    cv.cvtColor(src, rgb, cv.COLOR_RGBA2RGB)
    cv.cvtColor(rgb, hsv, cv.COLOR_RGB2HSV)
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY)
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0)
    cv.Canny(blurred, edges, 40, 120)
    cv.dilate(edges, edges, kernel) // tolerance: quad sides within ~4px of an edge count

    const cols = src.cols
    const rows = src.rows
    const hd = hsv.data
    const ed = edges.data

    // Scene-adaptive "white": bright relative to the image's 95th percentile.
    const totalPx = cols * rows
    const hist = new Array(256).fill(0)
    for (let i = 0; i < totalPx; i++) hist[hd[i * 3 + 2]]++
    let acc = 0
    let v95 = 255
    for (let v = 0; v < 256; v++) {
      acc += hist[v]
      if (acc >= totalPx * 0.95) {
        v95 = v
        break
      }
    }
    const whiteVal = Math.max(60, v95 * 0.55)
    // Strict paper threshold: near the scene's bright end. Glare sheen on a
    // table reads "white-ish" under whiteVal but sits below true paper.
    const strictVal = Math.max(whiteVal, v95 * 0.85)
    // Fraction of samples along side a->b that show boundary evidence:
    // the sample lies on a Canny edge, or (unless edgeOnly) the colors ~7px
    // to either side of the line differ (paper against background). (nx,ny)
    // must be the unit normal pointing toward the document interior.
    const sideSupport = (a, b, nx, ny, edgeOnly = false) => {
      let hit = 0
      let m = 0
      for (let t = 0; t <= 24; t++) {
        const px = a.x + ((b.x - a.x) * t) / 24
        const py = a.y + ((b.y - a.y) * t) / 24
        const x = Math.round(px)
        const y = Math.round(py)
        if (x < 3 || y < 3 || x >= cols - 3 || y >= rows - 3) continue
        m++
        if (ed[y * cols + x]) {
          hit++
          continue
        }
        if (edgeOnly) continue
        const off = 7
        const xi = Math.round(px + nx * off)
        const yi = Math.round(py + ny * off)
        const xo = Math.round(px - nx * off)
        const yo = Math.round(py - ny * off)
        if (xi < 0 || yi < 0 || xi >= cols || yi >= rows) continue
        if (xo < 0 || yo < 0 || xo >= cols || yo >= rows) continue
        const ii = (yi * cols + xi) * 3
        const io = (yo * cols + xo) * 3
        const dS = Math.abs(hd[ii + 1] - hd[io + 1])
        const dV = Math.abs(hd[ii + 2] - hd[io + 2])
        if (dS + dV > 20) hit++
      }
      return m ? hit / m : 1
    }

    let best = null
    let bestScore = 0
    let bestQuality = 0
    for (const cand of candidates) {
      const pts = orderCorners(cand.pts)
      const [tl, tr, br, bl] = pts

      // orderCorners' sum/diff heuristic can emit duplicate corners for
      // diagonal (diamond-oriented) quads; such degenerate output must not
      // be scored (a zero-length side trivially maxes side support).
      if (!isReasonableQuad(pts)) continue

      // Interior whiteness: sample a grid inset from the borders (so document
      // text/graphics and slight corner misfits don't dominate).
      let white = 0
      let strictWhite = 0
      let n = 0
      const vs = []
      const colWhite = new Array(12).fill(0)
      const colN = new Array(12).fill(0)
      const rowWhite = new Array(12).fill(0)
      const rowN = new Array(12).fill(0)
      for (let ui = 0; ui < 12; ui++) {
        for (let vi = 0; vi < 12; vi++) {
          const u = 0.12 + (0.76 * ui) / 11
          const v = 0.12 + (0.76 * vi) / 11
          const topX = tl.x + (tr.x - tl.x) * u
          const topY = tl.y + (tr.y - tl.y) * u
          const botX = bl.x + (br.x - bl.x) * u
          const botY = bl.y + (br.y - bl.y) * u
          const x = Math.round(topX + (botX - topX) * v)
          const y = Math.round(topY + (botY - topY) * v)
          if (x < 0 || y < 0 || x >= cols || y >= rows) continue
          n++
          colN[ui]++
          rowN[vi]++
          const idx = (y * cols + x) * 3
          vs.push(hd[idx + 2])
          if (hd[idx + 1] <= 60 && hd[idx + 2] >= strictVal) {
            strictWhite++
            colWhite[ui]++
            rowWhite[vi]++
          }
          if (hd[idx + 1] <= 110 && hd[idx + 2] >= whiteVal) white++
        }
      }
      if (!n) continue
      const whiteness = white / n
      const strictness = strictWhite / n
      // Structural strict whiteness: a real document contains truly-bright
      // paper along every grid column OR every grid row. Printed text is
      // thin (columns keep strict-white between lines) and a shadow band
      // kills rows but not columns. A quad padded with glare sheen or
      // background has entire corner columns AND rows without true paper.
      let minColW = 1
      let minRowW = 1
      for (let i = 0; i < 12; i++) {
        if (colN[i]) minColW = Math.min(minColW, colWhite[i] / colN[i])
        if (rowN[i]) minRowW = Math.min(minRowW, rowWhite[i] / rowN[i])
      }
      const structure = Math.max(minColW, minRowW)
      // Median interior brightness relative to the scene's bright end.
      // Paper is among the brightest surfaces, so a real document scores ~1;
      // a quad that ropes in dimmer surroundings (e.g. glare sheen on a dark
      // table around a receipt) drags its median down and loses. Capped at
      // 0.75 so a shadow across a document (which dims its median but not
      // its identity) is not punished — beyond "clearly bright" more
      // brightness must not win.
      vs.sort((a, b) => a - b)
      const brightness = Math.min(1, vs[vs.length >> 1] / (v95 || 1))
      const entry = diag && {
        pts,
        areaRatio: cand.area / (cols * rows),
        whiteness: +whiteness.toFixed(2),
        strictness: +strictness.toFixed(2),
        brightness: +brightness.toFixed(2),
        structure: +structure.toFixed(2),
      }
      if (entry) diag.push(entry)
      if (whiteness < 0.55) {
        if (entry) entry.rejected = 'whiteness'
        continue
      }
      if (structure < 0.15) {
        if (entry) entry.rejected = 'structure'
        continue
      }

      const qcx = (tl.x + tr.x + br.x + bl.x) / 4
      const qcy = (tl.y + tr.y + br.y + bl.y) / 4
      let minSide = 1
      let supportSum = 0
      const supVals = []
      for (let s = 0; s < 4; s++) {
        const a = pts[s]
        const b = pts[(s + 1) % 4]
        // Unit normal pointing toward the quad interior.
        const len = Math.hypot(b.x - a.x, b.y - a.y) || 1
        let nx = -(b.y - a.y) / len
        let ny = (b.x - a.x) / len
        const mx = (a.x + b.x) / 2
        const my = (a.y + b.y) / 2
        if ((qcx - mx) * nx + (qcy - my) * ny < 0) {
          nx = -nx
          ny = -ny
        }
        const sup = sideSupport(a, b, nx, ny)
        supVals.push(sup)
        supportSum += sup
        if (sup < minSide) minSide = sup
      }
      if (entry) entry.sides = supVals.map((v) => +v.toFixed(2))
      if (minSide < 0.4) {
        if (entry) entry.rejected = 'minSide'
        continue
      }

      // Whiteness is capped in the score: beyond "clearly paper" more white
      // must not win, or a document's blank sub-region (e.g. the unprinted
      // half of a receipt) would beat the full document, which scores lower
      // only because printed text darkens its interior samples.
      const score = Math.min(whiteness, 0.75) * brightness * brightness * (supportSum / 4) * Math.sqrt(cand.area)
      if (entry) entry.score = Math.round(score)
      if (score > bestScore) {
        bestScore = score
        best = pts
        // Size-independent quality of the winning quad, used by the UI as a
        // confidence signal: how paper-like the interior is and how well
        // every side sits on a real boundary. Junk quads that squeak past
        // the gates hover near the gate floors (0.55 * 0.4-ish sides);
        // genuine documents land well above.
        bestQuality = whiteness * (supportSum / 4)
      }
    }

    if (!best) return null

    // Refinement: contour strategies often truncate a document at an interior
    // shadow or fold (a receipt's curled blank tail, a hand's shadow band —
    // which can eat half the document). Push each side outward while the
    // moved side still shows boundary support and the swept strip still
    // looks like paper; keep the farthest position that qualifies.
    // The strip test keys on SATURATION, not brightness: shadowed paper dims
    // (V drops) but stays nearly achromatic (S ≤ ~10), while even white-ish
    // backgrounds (marble, wood sheen) carry noticeably more color. This is
    // what lets the sweep cross a deep shadow without bleeding onto the
    // table.
    const maxExtend = Math.round(Math.min(cols, rows) * 0.5)
    const stripWhiteVal = Math.max(50, v95 * 0.45) // tolerate shadowed paper
    for (let s = 0; s < 4; s++) {
      const a = best[s]
      const b = best[(s + 1) % 4]
      const qcx = (best[0].x + best[1].x + best[2].x + best[3].x) / 4
      const qcy = (best[0].y + best[1].y + best[2].y + best[3].y) / 4
      const len = Math.hypot(b.x - a.x, b.y - a.y) || 1
      // Outward normal (away from the quad interior).
      let nx = -(b.y - a.y) / len
      let ny = (b.x - a.x) / len
      const mx = (a.x + b.x) / 2
      const my = (a.y + b.y) / 2
      if ((qcx - mx) * nx + (qcy - my) * ny > 0) {
        nx = -nx
        ny = -ny
      }
      // Never push past a side that already sits on a strong physical edge —
      // that edge IS the document boundary. Only refine weakly-supported
      // sides (truncations at shadows/folds have little Canny support).
      if (sideSupport(a, b, -nx, -ny, true) >= 0.5) continue
      let bestD = 0
      for (let d = 3; d <= maxExtend; d += 3) {
        const a2 = { x: a.x + nx * d, y: a.y + ny * d }
        const b2 = { x: b.x + nx * d, y: b.y + ny * d }
        // Stop at the frame: beyond it sideSupport has no samples and would
        // report vacuous full support.
        if (a2.x < 0 || a2.y < 0 || a2.x >= cols || a2.y >= rows) break
        if (b2.x < 0 || b2.y < 0 || b2.x >= cols || b2.y >= rows) break
        // The moved side must itself sit on a document boundary.
        if (sideSupport(a2, b2, -nx, -ny) < 0.5) continue
        // The swept strip (between old and new side) must be paper-like.
        // Shadowed paper dims (lower V) but stays low-saturation, whereas
        // wood/fabric backgrounds are saturated — so the saturation bound
        // here is much stricter than the interior whiteness test.
        let white = 0
        let m = 0
        for (let t = 1; t < 24; t++) {
          const x = Math.round(a.x + ((b.x - a.x) * t) / 24 + (nx * d) / 2)
          const y = Math.round(a.y + ((b.y - a.y) * t) / 24 + (ny * d) / 2)
          if (x < 0 || y < 0 || x >= cols || y >= rows) continue
          m++
          const idx = (y * cols + x) * 3
          if (hd[idx + 1] <= 10 && hd[idx + 2] >= 60) white++
        }
        if (!m || white / m < 0.6) continue
        bestD = d
      }
      if (bestD) {
        best[s] = { x: Math.round(a.x + nx * bestD), y: Math.round(a.y + ny * bestD) }
        best[(s + 1) % 4] = { x: Math.round(b.x + nx * bestD), y: Math.round(b.y + ny * bestD) }
        continue
      }
      // Inward refinement (mirror case): a weakly-supported side that also
      // fails to find a boundary outward is probably overshooting the
      // document (e.g. resting on glare bloom next to it). Pull it inward
      // to the nearest strongly-edged line, provided the abandoned strip is
      // not true paper (never give up actual document surface).
      const maxShrink = Math.round(Math.min(cols, rows) * 0.2)
      const strictStrip = Math.max(whiteVal, v95 * 0.85)
      for (let d = 3; d <= maxShrink; d += 3) {
        const a2 = { x: a.x - nx * d, y: a.y - ny * d }
        const b2 = { x: b.x - nx * d, y: b.y - ny * d }
        // The abandoned strip must not be true paper.
        let white = 0
        let m = 0
        for (let t = 1; t < 24; t++) {
          const x = Math.round(a.x + ((b.x - a.x) * t) / 24 - (nx * d) / 2)
          const y = Math.round(a.y + ((b.y - a.y) * t) / 24 - (ny * d) / 2)
          if (x < 0 || y < 0 || x >= cols || y >= rows) continue
          m++
          const idx = (y * cols + x) * 3
          if (hd[idx + 1] <= 60 && hd[idx + 2] >= strictStrip) white++
        }
        if (m && white / m > 0.3) break
        if (sideSupport(a2, b2, nx, ny, true) >= 0.6) {
          // Don't collapse the quad below the minimum area.
          const trial = best.map((p, i) =>
            i === s || i === (s + 1) % 4 ? { x: Math.round(p.x - nx * d), y: Math.round(p.y - ny * d) } : p
          )
          if (quadArea(trial) >= cols * rows * 0.08) {
            best[s] = trial[s]
            best[(s + 1) % 4] = trial[(s + 1) % 4]
          }
          break
        }
      }
    }
    // Non-enumerable so the corners array still compares/serializes as a
    // plain 4-point array everywhere else.
    Object.defineProperty(best, 'confidence', { value: bestQuality, enumerable: false })
    return best
  } finally {
    rgb.delete()
    hsv.delete()
    gray.delete()
    blurred.delete()
    edges.delete()
    kernel.delete()
  }
}

/**
 * Find document corners in an RGBA cv.Mat using multiple detection strategies.
 * Candidate quads are gathered from a white-region strategy plus several
 * edge/threshold strategies, then ranked by interior whiteness and border
 * edge support (see pickBestCandidate).
 *
 * Returns [{x,y} x4] ordered TL,TR,BR,BL, or null if no document found.
 * minAreaRatio: quad must cover at least this fraction of the frame.
 */
export function findDocumentCorners(cv, src, minAreaRatio = 0.08, diag) {
  const frameArea = src.cols * src.rows
  const minArea = frameArea * minAreaRatio
  // Reject quads that essentially cover the whole frame — those are the frame
  // itself, not a document within it.
  const maxArea = frameArea * 0.95
  // Multiple epsilon values for polygon approximation (smaller = tighter fit)
  const epsilons = [0.02, 0.03, 0.04, 0.015, 0.05]

  // The scoring pipeline needs color/hull ops that simplified cv builds (and
  // the unit-test mock) lack; fall back to "largest reasonable quad" there.
  const canScore =
    typeof cv.inRange === 'function' &&
    typeof cv.convexHull === 'function' &&
    typeof cv.Scalar === 'function'

  const candidates = canScore ? collectWhiteRegionQuads(cv, src, minArea, maxArea) : []
  if (canScore) collectHoughQuads(cv, src, minArea, maxArea, candidates)

  // Strategy 1: Classic Canny with multiple thresholds
  const cannyParams = [
    [50, 150],   // Standard
    [30, 100],   // Lower thresholds for low contrast
    [75, 200],   // Higher thresholds for noisy backgrounds
    [20, 60],    // Very low for faint edges
  ]

  // Strategy 2: Adaptive threshold (good for uneven lighting)
  // Strategy 3: Simple threshold on blurred + morphological close

  const gray = new cv.Mat()
  const blurred = new cv.Mat()
  const edges = new cv.Mat()
  const morphed = new cv.Mat()
  const dilateKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3))
  const closeKernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(7, 7))
  const contours = new cv.MatVector()
  const hierarchy = new cv.Mat()

  let bestResult = null

  try {
    cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY)
    cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0)

    // Strategy 1: Canny edge detection with multiple threshold pairs
    // Note: cv.findContours() clears and refills the MatVector on each call (standard OpenCV behavior)
    for (const [low, high] of cannyParams) {
      cv.Canny(blurred, edges, low, high)
      cv.dilate(edges, edges, dilateKernel)
      cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE)

      if (canScore) {
        collectQuadsFromContours(cv, contours, minArea, epsilons, maxArea, candidates)
      } else {
        const result = findBestQuadFromContours(cv, contours, bestResult ? bestResult.area : minArea, epsilons, maxArea)
        if (result && isReasonableQuad(result.pts)) {
          bestResult = result
        }
      }
    }

    // Strategy 2: Adaptive threshold to handle uneven lighting
    cv.adaptiveThreshold(blurred, edges, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY_INV, 11, 2)
    cv.morphologyEx(edges, morphed, cv.MORPH_CLOSE, closeKernel)
    cv.findContours(morphed, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE)

    if (canScore) {
      collectQuadsFromContours(cv, contours, minArea, epsilons, maxArea, candidates)
    } else {
      const adaptiveResult = findBestQuadFromContours(cv, contours, bestResult ? bestResult.area : minArea, epsilons, maxArea)
      if (adaptiveResult && isReasonableQuad(adaptiveResult.pts)) {
        bestResult = adaptiveResult
      }
    }

    // Strategy 3: Large Gaussian blur + Otsu threshold + morphological close
    const largeBlur = new cv.Mat()
    cv.GaussianBlur(gray, largeBlur, new cv.Size(11, 11), 0)
    cv.threshold(largeBlur, edges, 0, 255, cv.THRESH_BINARY_INV + cv.THRESH_OTSU)
    cv.morphologyEx(edges, morphed, cv.MORPH_CLOSE, closeKernel)
    cv.dilate(morphed, morphed, dilateKernel)
    cv.findContours(morphed, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE)
    largeBlur.delete()

    if (canScore) {
      collectQuadsFromContours(cv, contours, minArea, epsilons, maxArea, candidates)
      return pickBestCandidate(cv, src, candidates, diag)
    }

    const otsuResult = findBestQuadFromContours(cv, contours, bestResult ? bestResult.area : minArea, epsilons, maxArea)
    if (otsuResult && isReasonableQuad(otsuResult.pts)) {
      bestResult = otsuResult
    }

    if (!bestResult) return null
    return orderCorners(bestResult.pts)
  } finally {
    gray.delete()
    blurred.delete()
    edges.delete()
    morphed.delete()
    dilateKernel.delete()
    closeKernel.delete()
    contours.delete()
    hierarchy.delete()
  }
}

/** Perspective-warp the quad defined by corners (source-image coords) to a flat canvas. */
export function warpToCorners(cv, srcCanvas, corners) {
  const [tl, tr, br, bl] = corners
  const dist = (a, b) => Math.hypot(a.x - b.x, a.y - b.y)
  const w = Math.max(1, Math.round(Math.max(dist(tl, tr), dist(bl, br))))
  const h = Math.max(1, Math.round(Math.max(dist(tl, bl), dist(tr, br))))

  const src = cv.imread(srcCanvas)
  const dst = new cv.Mat()
  const srcPts = cv.matFromArray(4, 1, cv.CV_32FC2, [tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y])
  const dstPts = cv.matFromArray(4, 1, cv.CV_32FC2, [0, 0, w, 0, w, h, 0, h])
  const M = cv.getPerspectiveTransform(srcPts, dstPts)
  cv.warpPerspective(src, dst, M, new cv.Size(w, h), cv.INTER_LINEAR, cv.BORDER_REPLICATE)

  const out = document.createElement('canvas')
  cv.imshow(out, dst)
  src.delete()
  dst.delete()
  M.delete()
  srcPts.delete()
  dstPts.delete()
  return out
}

/** Apply a document filter: "color" | "grayscale" | "bw". Returns a canvas. */
export function applyFilter(cv, canvas, filter) {
  if (filter === 'color') return canvas
  const src = cv.imread(canvas)
  const dst = new cv.Mat()
  cv.cvtColor(src, dst, cv.COLOR_RGBA2GRAY)
  if (filter === 'bw') {
    cv.adaptiveThreshold(dst, dst, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 21, 10)
  }
  const out = document.createElement('canvas')
  cv.imshow(out, dst)
  src.delete()
  dst.delete()
  return out
}

export function loadImage(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = dataUrl
  })
}

export function canvasFromImage(img) {
  const c = document.createElement('canvas')
  c.width = img.naturalWidth || img.width
  c.height = img.naturalHeight || img.height
  c.getContext('2d').drawImage(img, 0, 0)
  return c
}

/** Full-image corners with a small inset, as a fallback crop. */
export function defaultCorners(w, h, inset = 0.04) {
  const dx = w * inset
  const dy = h * inset
  return [
    { x: dx, y: dy },
    { x: w - dx, y: dy },
    { x: w - dx, y: h - dy },
    { x: dx, y: h - dy },
  ]
}

/** Rotate a canvas clockwise by a multiple of 90 degrees. Returns a canvas. */
export function rotateCanvas(canvas, degrees) {
  const angle = ((Math.round(degrees / 90) * 90) % 360 + 360) % 360
  if (angle === 0) return canvas
  const swap = angle === 90 || angle === 270
  const out = document.createElement('canvas')
  out.width = swap ? canvas.height : canvas.width
  out.height = swap ? canvas.width : canvas.height
  const ctx = out.getContext('2d')
  ctx.translate(out.width / 2, out.height / 2)
  ctx.rotate((angle * Math.PI) / 180)
  ctx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2)
  return out
}

/** Render a page (original + corners + filter + rotation) to a JPEG data URL. */
export async function processPage(cv, page) {
  const img = await loadImage(page.originalDataUrl)
  const canvas = canvasFromImage(img)
  const corners = page.corners || defaultCorners(canvas.width, canvas.height, 0)
  const warped = warpToCorners(cv, canvas, corners)
  const filtered = applyFilter(cv, warped, page.filter)
  const rotated = rotateCanvas(filtered, page.rotation || 0)
  return rotated.toDataURL('image/jpeg', 0.92)
}
